import type { CandidateCause, Incident, IncidentEvent } from '../../services/incidentService';

/**
 * Plain-text rendering of incidents (Reef Guard #1824) — shared by the pages
 * and the Markdown export, so the export says exactly what the screen says.
 */

export type T = (key: string, opts?: Record<string, unknown>) => unknown;

const str = (t: T, key: string, opts?: Record<string, unknown>) => String(t(key, opts));

export function formatNumber(value: number | string | null | undefined, language: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(n) : '';
}

export function parameterName(t: T, id: string): string {
  return str(t, `measurement.history.parameterNames.${id}`, { defaultValue: id });
}

/** Local 'YYYY-MM-DD' of an ISO timestamp (the day an event is grouped under). */
export function localDay(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDay(day: string, language: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1),
  );
}

export function incidentTitle(t: T, incident: Incident): string {
  const label = incident.triggerType === 'critical_finding' && incident.triggerLabel ? parameterName(t, incident.triggerLabel) : incident.triggerLabel;
  return str(t, `incidents.trigger.${incident.triggerType}`, { name: label ?? '' });
}

export function eventText(t: T, event: IncidentEvent, language: string): string {
  const v = event.values;
  switch (event.type) {
    case 'measurement':
      return str(t, 'incidents.events.measurement', {
        parameter: parameterName(t, String(v.parameterId ?? '')),
        value: formatNumber(v.value, language),
        unit: v.unit ?? '',
      });
    case 'dosing':
      return str(t, 'incidents.events.dosing', {
        product: event.title ?? '',
        amount: formatNumber(v.amount, language),
        unit: v.unit ?? '',
        count: Number(v.doses ?? 1),
        doses: Number(v.doses ?? 1),
      });
    case 'finding':
      return str(t, 'incidents.events.finding', {
        parameter: parameterName(t, String(v.parameterId ?? '')),
        severity: str(t, `reefGuard.severity.${String(v.severity)}`),
      });
    case 'intervention':
      return [str(t, 'incidents.events.intervention', { kind: str(t, `incidents.interventionKinds.${String(v.kind)}`) }), event.title]
        .filter(Boolean)
        .join(' — ');
    case 'image':
    case 'icp_analysis':
      return str(t, `incidents.events.${event.type}`);
    default:
      return str(t, `incidents.events.${event.type}`, { name: event.title ?? '' });
  }
}

export function causeText(t: T, cause: CandidateCause, language: string): string {
  const v = cause.values;
  const parameters = cause.parameterIds.map((id) => parameterName(t, id)).join(', ');
  switch (cause.rule) {
    case 'swing':
      return str(t, cause.parameterIds.length > 1 ? 'incidents.causes.swing_correlated' : 'incidents.causes.swing', {
        parameters,
        parameter: parameterName(t, String(v.parameter)),
        from: formatNumber(v.from, language),
        to: formatNumber(v.to, language),
        hours: formatNumber(v.hours, language),
      });
    case 'out_of_range':
      return str(t, `incidents.causes.out_of_range_${v.direction === 'low' ? 'low' : 'high'}`, {
        parameter: parameters,
        value: formatNumber(v.value, language),
        min: formatNumber(v.min, language),
        max: formatNumber(v.max, language),
        readings: Number(v.count),
      });
    case 'livestock_cluster':
      return str(t, 'incidents.causes.livestock_cluster', { count: Number(v.count), days: Number(v.days) });
    case 'dose_spike':
      return str(t, 'incidents.causes.dose_spike', {
        product: String(v.product),
        amount: formatNumber(v.amount, language),
        typical: formatNumber(v.typical, language),
        unit: String(v.unit),
      });
    case 'guard_finding':
      return str(t, 'incidents.causes.guard_finding', { parameter: parameters });
    case 'recent_change':
      return str(t, 'incidents.causes.recent_change', { kind: str(t, `incidents.interventionKinds.${String(v.kind)}`) });
    default:
      return str(t, 'incidents.causes.no_tests', { days: Number(v.days) });
  }
}

export function whenText(t: T, cause: CandidateCause): string {
  return cause.daysBefore === 0 ? str(t, 'incidents.sameDay') : str(t, 'incidents.daysBefore', { count: cause.daysBefore, days: cause.daysBefore });
}
